import { Aviso, Botao } from '@mymaxx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle2,
  Coffee,
  Download,
  Ear,
  Power,
  Share2,
  ToggleLeft,
  ToggleRight,
  UserPlus,
  Users,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { ListaConversas } from '../atendimento/ListaConversas';
import { ModalEncerrar, ModalTransferir } from '../atendimento/ModaisAtendimento';
import { Conversa } from '../atendimento/Conversa';
import { PainelCliente } from '../atendimento/PainelCliente';
import { LevarAsConversas } from '../atendimento/LevarAsConversas';
import { ErroApi } from '../lib/api';
import { ModalPausa } from '../atendimento/ModalPausa';
import { useSessao } from '../lib/sessao';
import { atendimentoApi } from '../lib/atendimento-api';
import { useSussurros } from '../lib/sussurros';
import { useTempoRealEquipe } from '../lib/tempo-real';

type Aba = 'minhas' | 'fila' | 'andamento' | 'encerradas';

/** A tela de trabalho do atendente: fila à esquerda, conversa no meio, cliente à direita. */
export function Atendimentos() {
  const queryClient = useQueryClient();
  const [aba, setAba] = useState<Aba>('minhas');
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [aviso, setAviso] = useState<{ tipo: 'sucesso' | 'erro'; texto: string } | null>(null);
  const eu = useSessao((e) => e.usuario);
  const podeVerSetor = !!eu?.permissoes.includes('conversas.ver_setor');
  const [statusLocal, setStatusLocal] = useState(eu?.statusPresenca ?? 'offline');
  const [pausaDesde, setPausaDesde] = useState<string | null>(eu?.pausaIniciadaEm ?? null);
  const [transferindo, setTransferindo] = useState(false);
  const [encerrando, setEncerrando] = useState(false);
  const abrirSussurro = useSussurros((e) => e.abrir);

  const resumo = useQuery({
    queryKey: ['atendimento-resumo'],
    queryFn: atendimentoApi.resumo,
    refetchInterval: 30_000,
  });
  const minhas = useQuery({ queryKey: ['atendimento-minhas'], queryFn: atendimentoApi.minhas });
  const fila = useQuery({ queryKey: ['atendimento-fila'], queryFn: () => atendimentoApi.fila() });
  const encerradas = useQuery({
    queryKey: ['atendimento-encerradas', busca],
    queryFn: () => atendimentoApi.encerradas(busca || undefined),
    enabled: aba === 'encerradas',
  });
  // O que está em curso no setor. Só para quem supervisiona — a aba nem aparece para os
  // outros, e a API recusa de qualquer jeito.
  const andamento = useQuery({
    queryKey: ['atendimento-em-andamento'],
    queryFn: atendimentoApi.emAndamento,
    enabled: podeVerSetor,
    refetchInterval: 30_000,
  });
  const detalhe = useQuery({
    queryKey: ['conversa', selecionada],
    queryFn: () => atendimentoApi.detalhe(selecionada!),
    enabled: !!selecionada,
  });
  // Uma chamada só, e por uma rota que o atendente pode ler: as de /v1/gestao exigem
  // permissão de administração e devolviam 403 para quem só atende.
  const catalogos = useQuery({
    queryKey: ['catalogos-atendimento'],
    queryFn: atendimentoApi.catalogos,
  });
  const setores = { data: catalogos.data?.setores };
  const rapidas = { data: catalogos.data?.mensagensRapidas };
  const motivos = { data: catalogos.data?.motivos };

  const recarregar = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['atendimento-minhas'] });
    void queryClient.invalidateQueries({ queryKey: ['atendimento-fila'] });
    void queryClient.invalidateQueries({ queryKey: ['atendimento-em-andamento'] });
    void queryClient.invalidateQueries({ queryKey: ['atendimento-resumo'] });
    void queryClient.invalidateQueries({ queryKey: ['conversa'] });
  }, [queryClient]);

  useTempoRealEquipe(selecionada, {
    aoReceberMensagem: recarregar,
    aoMudarConversa: recarregar,
  });

  const presenca = useMutation({
    mutationFn: (status: 'online' | 'pausa') => atendimentoApi.definirPresenca(status),
    onSuccess: (_, status) => {
      setStatusLocal(status);
      setPausaDesde(status === 'pausa' ? new Date().toISOString() : null);
      setAviso({
        tipo: 'sucesso',
        texto: status === 'pausa' ? 'Você está em pausa.' : 'Você está online.',
      });
    },
  });

  const puxar = useMutation({
    mutationFn: () => atendimentoApi.puxarProxima(),
    onSuccess: (conversa) => {
      setSelecionada(conversa.id);
      setAba('minhas');
      recarregar();
    },
    onError: (e) =>
      setAviso({ tipo: 'erro', texto: e instanceof ErroApi ? e.mensagem : 'Falha ao puxar' }),
  });

  const distribuicao = useMutation({
    mutationFn: ({ setorId, automatica }: { setorId: string; automatica: boolean }) =>
      atendimentoApi.definirDistribuicao(setorId, automatica),
    onSuccess: (_, { automatica }) => {
      // A chave certa: os setores desta tela vêm do catálogo do atendimento. Invalidar
      // 'setores' (que é de outra tela) não atualizava nada, e o botão só ficava verde
      // depois de recarregar a página.
      void queryClient.invalidateQueries({ queryKey: ['catalogos-atendimento'] });
      recarregar();
      setAviso({
        tipo: 'sucesso',
        texto: automatica
          ? 'Fila automática ligada: quem está livre recebe sozinho.'
          : 'Fila automática desligada: a equipe puxa da fila.',
      });
    },
    onError: (e) =>
      setAviso({
        tipo: 'erro',
        texto: e instanceof ErroApi ? e.mensagem : 'Não consegui mudar a distribuição',
      }),
  });

  const assumir = useMutation({
    mutationFn: (id: string) => atendimentoApi.assumir(id),
    onSuccess: () => {
      setAba('minhas');
      recarregar();
    },
    onError: (e) =>
      setAviso({ tipo: 'erro', texto: e instanceof ErroApi ? e.mensagem : 'Falha ao assumir' }),
  });

  /** Entrar junto: o titular continua, e os dois falam com o cliente. */
  const participar = useMutation({
    mutationFn: (id: string) => atendimentoApi.participar(id),
    onSuccess: () => {
      recarregar();
      setAviso({ tipo: 'sucesso', texto: 'Você entrou no atendimento.' });
    },
    onError: (e) =>
      setAviso({ tipo: 'erro', texto: e instanceof ErroApi ? e.mensagem : 'Falha ao entrar' }),
  });

  const lista =
    aba === 'minhas'
      ? (minhas.data ?? [])
      : aba === 'fila'
        ? (fila.data ?? [])
        : aba === 'andamento'
          ? (andamento.data ?? [])
          : (encerradas.data ?? []);

  const conversa = detalhe.data;
  const naFila = resumo.data?.filaPorSetor.reduce((total, s) => total + s.naFila, 0) ?? 0;
  const meusSetores = new Set((resumo.data?.filaPorSetor ?? []).map((s) => s.setorId));

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Mesma regra da estação: em pausa, a tela sai do caminho. */}
      {statusLocal === 'pausa' && (
        <ModalPausa
          motivo={eu?.motivoPausa ?? null}
          desdeIso={pausaDesde}
          voltando={presenca.isPending}
          onVoltar={() => presenca.mutate('online')}
        />
      )}
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">Atendimentos</h1>
          <p className="text-xs text-neutral-500">
            {resumo.data
              ? `${resumo.data.minhasAtivas} de ${resumo.data.limiteSimultaneo} em andamento · ${naFila} na fila`
              : 'Carregando…'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Botao
            variante="secundario"
            onClick={() => presenca.mutate('pausa')}
            carregando={presenca.isPending}
          >
            <Coffee size={15} /> Pausa
          </Botao>
          <Botao variante="secundario" onClick={() => presenca.mutate('online')}>
            <Power size={15} /> Online
          </Botao>
          <Botao onClick={() => puxar.mutate()} carregando={puxar.isPending}>
            <Download size={15} /> Puxar próxima
          </Botao>
        </div>
      </header>

      {/* Quem manda no setor liga e desliga a fila automática daqui mesmo. */}
      {(setores.data ?? []).filter((s) => meusSetores.has(s.id)).length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-neutral-500">Fila automática:</span>
          {(setores.data ?? [])
            .filter((s) => meusSetores.has(s.id))
            .map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() =>
                  distribuicao.mutate({ setorId: s.id, automatica: !s.distribuicaoAutomatica })
                }
                className={`flex items-center gap-1 rounded-full border px-2 py-1 transition ${
                  s.distribuicaoAutomatica
                    ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200'
                    : 'border-neutral-300 text-neutral-500 dark:border-neutral-700'
                }`}
                title={
                  s.distribuicaoAutomatica
                    ? 'Ligada: a fila cai sozinha em quem está livre'
                    : 'Desligada: a equipe puxa da fila'
                }
              >
                {s.distribuicaoAutomatica ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                {s.nome}
              </button>
            ))}
        </div>
      )}

      {aviso && (
        <div className="mb-2">
          <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>
        </div>
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800">
        {/* Coluna 1: listas */}
        <div
          className={`${selecionada ? 'hidden md:flex' : 'flex'} w-full flex-col border-r border-neutral-200 md:w-64 md:shrink-0 lg:w-72 xl:w-80 2xl:w-96 dark:border-neutral-800`}
        >
          <div className="flex border-b border-neutral-200 text-xs dark:border-neutral-800">
            {(
              [
                ['minhas', `Minhas (${minhas.data?.length ?? 0})`],
                ['fila', `Fila (${fila.data?.length ?? 0})`],
                ...(podeVerSetor
                  ? ([['andamento', `Em andamento (${andamento.data?.length ?? 0})`]] as const)
                  : []),
                ['encerradas', 'Encerradas'],
              ] as const
            ).map(([valor, rotulo]) => (
              <button
                key={valor}
                type="button"
                onClick={() => setAba(valor)}
                className={`flex-1 px-2 py-2 transition ${
                  aba === valor
                    ? 'border-b-2 border-marca-600 font-medium text-marca-700 dark:text-marca-300'
                    : 'text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-900'
                }`}
              >
                {rotulo}
              </button>
            ))}
          </div>

          {aba === 'encerradas' && (
            <div className="border-b border-neutral-200 p-2 dark:border-neutral-800">
              <input
                value={busca}
                placeholder="Protocolo, nome do cliente ou algo que foi dito"
                aria-label="Buscar nos atendimentos encerrados"
                onChange={(e) => setBusca(e.target.value)}
                className="w-full rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
              />
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            <ListaConversas
              conversas={lista}
              selecionada={selecionada}
              onSelecionar={setSelecionada}
              mostrarAtendente={aba === 'andamento'}
              vazio={
                aba === 'minhas'
                  ? 'Nenhum atendimento com você agora.'
                  : aba === 'fila'
                    ? 'Ninguém esperando. 🎉'
                    : aba === 'andamento'
                      ? 'Nada em curso nos seus setores.'
                      : busca
                        ? 'Nenhum atendimento com isso. Tente outra palavra, o nome do cliente ou o protocolo.'
                        : 'Nada encontrado.'
              }
            />
          </div>
        </div>

        {/* Coluna 2: conversa */}
        {conversa ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
              <button
                type="button"
                aria-label="Voltar para a lista"
                onClick={() => setSelecionada(null)}
                className="text-neutral-500 md:hidden"
              >
                <ArrowLeft size={18} />
              </button>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {conversa.cliente ?? 'Ainda sem identificação'}
                </p>
                <p className="truncate text-xs text-neutral-500">
                  {conversa.protocolo} · {conversa.setor?.nome ?? 'sem setor'}
                  {conversa.participantes.length > 1 && (
                    <>
                      {' · '}
                      <Users size={11} className="inline" />{' '}
                      {conversa.participantes
                        .map((p) => p.nome.split(/\s+/)[0] ?? p.nome)
                        .join(' e ')}
                    </>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                {conversa.status === 'fila' && (
                  <Botao onClick={() => assumir.mutate(conversa.id)} carregando={assumir.isPending}>
                    Assumir
                  </Botao>
                )}
                {conversa.status !== 'encerrada' && conversa.status !== 'fila' && (
                  <>
                    {/* Não estou dentro: entro para ajudar sem tirar quem está. */}
                    {!conversa.meuPapel && (
                      <Botao
                        onClick={() => participar.mutate(conversa.id)}
                        carregando={participar.isPending}
                      >
                        <UserPlus size={15} /> Entrar junto
                      </Botao>
                    )}
                    <Botao
                      variante="secundario"
                      onClick={() => abrirSussurro(conversa.id, conversa.protocolo)}
                      title="Falar com a equipe sobre este atendimento (o cliente não vê)"
                    >
                      <Ear size={15} /> Sussurrar
                    </Botao>
                    {/* Sussurrar é aqui dentro, com quem já está neste atendimento. Isto é
                        levar o assunto para fora dele: o grupo de Rotas, o técnico, quem
                        não tem nada a ver com este protocolo mas sabe a resposta. */}
                    <LevarAsConversas conversa={conversa} />
                    {conversa.meuPapel && (
                      <>
                        <Botao variante="secundario" onClick={() => setTransferindo(true)}>
                          <Share2 size={15} /> Transferir
                        </Botao>
                        <Botao variante="perigo" onClick={() => setEncerrando(true)}>
                          <CheckCircle2 size={15} /> Encerrar
                        </Botao>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            <Conversa
              conversa={conversa}
              mensagens={conversa.mensagens}
              respostasRapidas={(rapidas.data ?? [])
                .filter((r) => r.ativo)
                .map((r) => ({ id: r.id, titulo: r.titulo, texto: r.conteudo }))}
              onMudou={recarregar}
            />
          </div>
        ) : (
          <div className="hidden flex-1 items-center justify-center p-8 text-center text-sm text-neutral-500 md:flex">
            Escolha um atendimento à esquerda ou puxe o próximo da fila.
          </div>
        )}

        {/* Coluna 3: cliente */}
        {conversa && <PainelCliente conversa={conversa} onAnotou={recarregar} />}
      </div>

      {conversa && transferindo && (
        <ModalTransferir
          conversa={conversa}
          setores={(setores.data ?? []).map((s) => ({ id: s.id, nome: s.nome }))}
          onFechar={() => setTransferindo(false)}
          onPronto={() => {
            setTransferindo(false);
            setSelecionada(null);
            recarregar();
            setAviso({ tipo: 'sucesso', texto: 'Atendimento transferido.' });
          }}
        />
      )}

      {conversa && encerrando && (
        <ModalEncerrar
          conversa={conversa}
          motivos={(motivos.data ?? []).flatMap((c) =>
            c.motivos.map((m) => ({ id: m.id, nome: `${c.nome} · ${m.nome}` })),
          )}
          onFechar={() => setEncerrando(false)}
          onPronto={(escopo) => {
            setEncerrando(false);
            if (escopo === 'apenas_eu') setSelecionada(null);
            recarregar();
            setAviso({
              tipo: 'sucesso',
              texto:
                escopo === 'apenas_eu'
                  ? 'Você saiu do atendimento. Quem ficou continua com o cliente.'
                  : 'Atendimento encerrado.',
            });
          }}
        />
      )}
    </div>
  );
}
